import { SyncWorker } from './SyncWorker';
import { db, LOCAL_WORKSPACE_TENANT_ID } from '../database';

/**
 * Bootstrap singleton for the sync engine.
 *
 * One SyncWorker per browser tab. React components (useSync), services and
 * the TenantContext all import `syncWorker` from here so engine state is
 * shared. `startSyncEngine()` is idempotent and safe to call from multiple
 * mount effects.
 */

export const syncWorker = new SyncWorker();

let startPromise = null;

/**
 * Idempotently start the sync engine. Resolves once the engine is running
 * (listeners bound, initial health check kicked off). Never throws — an
 * unreachable PocketBase must not break offline boot.
 */
export async function startSyncEngine() {
  if (syncWorker.isRunning) return syncWorker;

  if (!startPromise) {
    startPromise = (async () => {
      try {
        await db.ensureReady();
        // Default to the Local Workspace pseudo-tenant until auth supplies
        // a real organization id. Keeps every query tenant-scoped from t0.
        if (!syncWorker.activeTenantId) {
          await syncWorker.setTenantContext(LOCAL_WORKSPACE_TENANT_ID);
        }
        await syncWorker.start();
      } catch (error) {
        console.error('Sync engine failed to start:', error);
        // Allow retry on next call.
        startPromise = null;
        throw error;
      }
    })();
  }

  return startPromise;
}

/** Stop the engine and reset the start promise (HMR / logout flows). */
export async function stopSyncEngine() {
  syncWorker.stop();
  startPromise = null;
}

/**
 * Point the engine at an authenticated tenant (called from TenantContext /
 * AuthContext after login or tenant switch).
 */
export async function activateTenant(tenantId) {
  await db.ensureReady();
  await syncWorker.setTenantContext(tenantId);
  if (!syncWorker.isRunning) {
    await startSyncEngine();
  }
  // Fresh tenant → immediate catch-up pull for that tenant's watermark.
  void syncWorker.reconcileNow('tenant-activated');
}
